import React from "react";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import {formatCurrency} from "../../utils/format";
import {PlanSummaryResponse} from "../models/PlanSummaryResponse";
import {COLOR_COMPARE_PLANS_EXCESS, COLOR_COMPARE_PLANS_SHORTFALL} from "../../constants/colors";
import PillTextComponent from "./PillTextComponent";

interface NetWorthOverTimeRowProps {
    comparePlan1PlanSummaryResponse: PlanSummaryResponse;
    comparePlan2PlanSummaryResponse: PlanSummaryResponse;
    comparePlan1GraphOptions: Highcharts.Options;
    comparePlan2GraphOptions: Highcharts.Options;
    differenceInFutureValueOfExcessAssets?: number;
    isComparePlan2: boolean;
    showPillText?: boolean;
}

const NetWorthOverTimeRow: React.FC<NetWorthOverTimeRowProps> = ({
                                                                     comparePlan1PlanSummaryResponse,
                                                                     comparePlan2PlanSummaryResponse,
                                                                     comparePlan1GraphOptions,
                                                                     comparePlan2GraphOptions,
                                                                     differenceInFutureValueOfExcessAssets,
                                                                     isComparePlan2,
                                                                     showPillText
                                                                 }) => {
    const unifiedMaxY = Math.max(
        ...(comparePlan1PlanSummaryResponse?.futureValueOfExcessAssetsByAge || [0]),
        ...(comparePlan2PlanSummaryResponse?.futureValueOfExcessAssetsByAge || [0])
    );

    const withUnifiedMaxY = (options: Highcharts.Options) => {
        let newYAxis;
        if (Array.isArray(options.yAxis)) {
            newYAxis = options.yAxis.map(axis => ({ ...axis, max: unifiedMaxY }));
        } else if (typeof options.yAxis === 'object' && options.yAxis !== null) {
            newYAxis = { ...options.yAxis, max: unifiedMaxY };
        } else {
            newYAxis = { max: unifiedMaxY };
        }
        return {
            ...options,
            yAxis: newYAxis
        };
    };

    const updatedComparePlan1GraphOptions = withUnifiedMaxY(comparePlan1GraphOptions);
    const updatedComparePlan2GraphOptions = withUnifiedMaxY(comparePlan2GraphOptions);

    return (
        <div>
            {!isComparePlan2 && (
                <div className="plan-summary-card">
                    {comparePlan1PlanSummaryResponse!.excessAssets > 0 ?
                        <>
                            <div style={{textAlign: "center"}}>
                                {formatCurrency(comparePlan1PlanSummaryResponse.futureValueOfExcessAssets)}
                            </div>
                            <HighchartsReact highcharts={Highcharts} options={updatedComparePlan1GraphOptions}/>
                        </>
                        : <>
                            <div className="asset_insufficiency_empty_state"> Net Worth Over Time cannot be displayed
                                when there is an asset shortfall.
                            </div>
                        </>
                    }
                </div>
            )}
            {isComparePlan2 && (
                <div className="plan-summary-card">
                    {comparePlan2PlanSummaryResponse  !=  null &&
                        <>
                            {comparePlan2PlanSummaryResponse.excessAssets > 0 &&
                                <>
                                    <div style={{textAlign: "center"}}>
                                        {formatCurrency(comparePlan2PlanSummaryResponse.futureValueOfExcessAssets)}
                                        {showPillText
                                            && (differenceInFutureValueOfExcessAssets)  !=  0
                                            && (comparePlan1PlanSummaryResponse.excessAssets > 0
                                                && comparePlan2PlanSummaryResponse.excessAssets > 0) &&
                                            <PillTextComponent
                                                color={(differenceInFutureValueOfExcessAssets!) >= 0 ? COLOR_COMPARE_PLANS_EXCESS : COLOR_COMPARE_PLANS_SHORTFALL}
                                                pillTextValue={(differenceInFutureValueOfExcessAssets!)}
                                                className={(differenceInFutureValueOfExcessAssets!) >= 0 ? "compare-plans-pill-container pill-container-excess " : "compare-plans-pill-container pill-container-shortfall"}/>
                                        }
                                    </div>
                                    <HighchartsReact highcharts={Highcharts} options={updatedComparePlan2GraphOptions}/>
                                </>
                            }
                            {comparePlan2PlanSummaryResponse.excessAssets <= 0 &&
                                <>
                                    <div className="asset_insufficiency_empty_state"> Net Worth Over Time cannot be
                                        displayed
                                        when there is an asset shortfall.
                                    </div>
                                </>
                            }
                        </>
                    }
                </div>
            )}
        </div>
    );
}

export default NetWorthOverTimeRow;